/**
 * Bangladesh Income Tax slabs by Fiscal Year.
 * Add a new entry to TAX_YEARS to support a new budget year.
 *
 * Source: NBR Finance Act. Verify with nbr.gov.bd before relying.
 */

export type AssesseeCategory =
  | "male"
  | "female"
  | "senior"
  | "disabled"
  | "freedomFighter"
  | "thirdGender"
  | "lessDeveloped";

export type Location = "dhaka_chittagong" | "other_city" | "other";

export interface Slab {
  /** Width of this slab in BDT. Use Infinity for the last "above" slab. */
  amount: number;
  /** Marginal rate, e.g. 0.05 for 5% */
  rate: number;
}

export interface SurchargeBracket {
  /** Net wealth threshold (BDT). Surcharge applies when wealth > threshold. */
  wealthAbove: number;
  /** Rate applied on tax (not on income). e.g. 0.10 for 10%. */
  rate: number;
}

export interface TaxYear {
  id: string; // e.g. "FY2025-26"
  fyLabel: { en: string; bn: string };
  ayLabel: { en: string; bn: string };
  /** Default exemption threshold (general male). */
  baseExemption: number;
  /** Per-category exemption override (BDT). Defaults to baseExemption. */
  categoryExemption: Partial<Record<AssesseeCategory, number>>;
  /** Slabs ABOVE the exemption threshold, applied progressively (general). */
  slabs: Slab[];
  /** Minimum tax by location. */
  minimumTax: Record<Location, number>;
  /** Investment rebate parameters. */
  rebate: {
    rate: number; // 0.15
    pctOfTaxableIncome: number; // 0.20
    absoluteCap: number; // 1.5 crore
  };
  /** Surcharge brackets, sorted ASCENDING. The highest matching bracket wins. */
  surcharge: SurchargeBracket[];
  /** Date last verified (ISO yyyy-mm-dd). */
  lastUpdated: string;
  /** Finance Act reference. */
  source: { en: string; bn: string };
}

const COMMON_SLABS: Slab[] = [
  { amount: 100_000, rate: 0.05 },
  { amount: 400_000, rate: 0.10 },
  { amount: 500_000, rate: 0.15 },
  { amount: 500_000, rate: 0.20 },
  { amount: 2_000_000, rate: 0.25 },
  { amount: Infinity, rate: 0.30 },
];

const COMMON_REBATE = { rate: 0.15, pctOfTaxableIncome: 0.20, absoluteCap: 15_000_000 };

const COMMON_SURCHARGE: SurchargeBracket[] = [
  { wealthAbove: 40_000_000, rate: 0.10 },
  { wealthAbove: 100_000_000, rate: 0.20 },
  { wealthAbove: 200_000_000, rate: 0.30 },
  { wealthAbove: 500_000_000, rate: 0.35 },
];

const COMMON_MIN_TAX: Record<Location, number> = {
  dhaka_chittagong: 5000,
  other_city: 4000,
  other: 3000,
};

export const TAX_YEARS: TaxYear[] = [
  {
    id: "FY2025-26",
    fyLabel: { en: "FY 2025-26", bn: "অর্থবছর ২০২৫-২৬" },
    ayLabel: { en: "AY 2026-27", bn: "কর বর্ষ ২০২৬-২৭" },
    baseExemption: 350_000,
    categoryExemption: {
      male: 350_000,
      thirdGender: 350_000,
      female: 400_000,
      senior: 400_000,
      disabled: 475_000,
      freedomFighter: 500_000,
      lessDeveloped: 350_000,
    },
    slabs: COMMON_SLABS,
    minimumTax: COMMON_MIN_TAX,
    rebate: COMMON_REBATE,
    surcharge: COMMON_SURCHARGE,
    lastUpdated: "2025-07-01",
    source: {
      en: "Indicative — based on Finance Act 2025 / NBR draft. Verify at nbr.gov.bd.",
      bn: "আনুমানিক — অর্থ আইন ২০২৫ / এনবিআর খসড়া অনুযায়ী। nbr.gov.bd থেকে যাচাই করুন।",
    },
  },
  {
    id: "FY2024-25",
    fyLabel: { en: "FY 2024-25", bn: "অর্থবছর ২০২৪-২৫" },
    ayLabel: { en: "AY 2025-26", bn: "কর বর্ষ ২০২৫-২৬" },
    baseExemption: 350_000,
    categoryExemption: {
      male: 350_000,
      thirdGender: 350_000,
      female: 400_000,
      senior: 400_000,
      disabled: 475_000,
      freedomFighter: 500_000,
      lessDeveloped: 350_000,
    },
    slabs: COMMON_SLABS,
    minimumTax: COMMON_MIN_TAX,
    rebate: COMMON_REBATE,
    surcharge: COMMON_SURCHARGE,
    lastUpdated: "2024-07-01",
    source: {
      en: "Based on Finance Act 2024. Verify at nbr.gov.bd.",
      bn: "অর্থ আইন ২০২৪ অনুযায়ী। nbr.gov.bd থেকে যাচাই করুন।",
    },
  },
];

export const CATEGORY_LABEL: Record<AssesseeCategory, { en: string; bn: string }> = {
  male: { en: "Male (general)", bn: "পুরুষ (সাধারণ)" },
  female: { en: "Female", bn: "মহিলা" },
  senior: { en: "Senior citizen (65+)", bn: "জ্যেষ্ঠ নাগরিক (৬৫+)" },
  disabled: { en: "Person with disability", bn: "প্রতিবন্ধী ব্যক্তি" },
  freedomFighter: { en: "Wounded freedom fighter (gazetted)", bn: "গেজেটেড আহত মুক্তিযোদ্ধা" },
  thirdGender: { en: "Third gender", bn: "তৃতীয় লিঙ্গ" },
  lessDeveloped: { en: "Resident in less developed area", bn: "অনগ্রসর এলাকার বাসিন্দা" },
};

export const LOCATION_LABEL: Record<Location, { en: string; bn: string }> = {
  dhaka_chittagong: { en: "Dhaka / Chittagong City Corp.", bn: "ঢাকা / চট্টগ্রাম সিটি কর্পোরেশন" },
  other_city: { en: "Other City Corporation", bn: "অন্যান্য সিটি কর্পোরেশন" },
  other: { en: "Other (district / upazila)", bn: "অন্যান্য (জেলা / উপজেলা)" },
};
